import {
  WEEK_ORDER,
  WEEKDAY_SHORT,
  formatDateKey,
  shiftDateKey,
  uaePublicHoliday,
  weekdayOf,
  type TimetableDraft,
} from "@teamna/shared";
import { Button } from "../../components/index.ts";
import type { BoardGroup } from "./WeekBoard.tsx";

/**
 * The Dates view: a month of the term. Each date shows its sessions as small bars in the level's
 * colour, closed dates in red, UAE public holidays in amber, days after the end of the term faded.
 * A click on a date opens it in the rail: close the business that day, or take one session off.
 */

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function monthOf(dateKey: string): string {
  return dateKey.slice(0, 7);
}

export function shiftMonth(month: string, by: number): string {
  const [year, index] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year!, index! - 1 + by, 1));
  return date.toISOString().slice(0, 7);
}

export function DatesBoard({
  draft,
  groups,
  today,
  lastDate,
  firstMonth,
  lastMonth,
  month,
  daysOff,
  selected,
  sessionCount,
  onMonth,
  onPick,
}: {
  draft: TimetableDraft;
  groups: Map<string, BoardGroup>;
  today: string;
  /** The last date sessions are counted to. */
  lastDate: string;
  firstMonth: string;
  lastMonth: string;
  month: string;
  daysOff: readonly number[];
  selected: string | null;
  sessionCount: number;
  onMonth: (month: string) => void;
  onPick: (date: string) => void;
}) {
  const first = `${month}-01`;
  const lead = WEEK_ORDER.indexOf(weekdayOf(first));
  const dates: string[] = [];
  for (let date = first; date.startsWith(month); date = shiftDateKey(date, 1)) dates.push(date);
  const [year, index] = month.split("-").map(Number);
  const closedDays = new Set(draft.closures.filter((row) => row.item === null).map((row) => row.date));

  return (
    <div className="grid max-w-[920px] gap-2.5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* One month on screen (the page's month switch moves between months): no arrows here. */}
        {firstMonth === lastMonth ? (
          <span className="text-[15px] font-bold">
            {MONTHS[index! - 1]} {year}
          </span>
        ) : (
          <div className="flex items-center gap-1 text-[15px] font-bold">
            <Button variant="ghost" size="sm" aria-label="Last month" disabled={month <= firstMonth} onClick={() => onMonth(shiftMonth(month, -1))}>
              ‹
            </Button>
            <span aria-live="polite">
              {MONTHS[index! - 1]} {year}
            </span>
            <Button variant="ghost" size="sm" aria-label="Next month" disabled={month >= lastMonth} onClick={() => onMonth(shiftMonth(month, 1))}>
              ›
            </Button>
          </div>
        )}
        <span className="text-[12px] text-ink-3 tabular-nums">
          {sessionCount} {sessionCount === 1 ? "session" : "sessions"} from {first > today ? formatDateKey(first) : "today"} to {formatDateKey(lastDate)}
          {draft.until === null ? ", then more each night" : ""}
        </span>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEK_ORDER.map((day) => (
          <div key={day} className="text-center text-[11px] font-bold text-ink-3">
            {WEEKDAY_SHORT[day]}
          </div>
        ))}
        {Array.from({ length: lead }, (_, slot) => (
          <div key={`lead-${slot}`} />
        ))}
        {dates.map((date) => {
          const weekday = weekdayOf(date);
          const dayNumber = Number(date.slice(8));
          if (daysOff.includes(weekday)) {
            return (
              <div
                key={date}
                className="min-h-[76px] rounded-[10px] border-[1.5px] border-line p-1.5 text-[12px] font-bold text-ink-3"
                style={{ backgroundImage: "repeating-linear-gradient(135deg, var(--color-sunken) 0 6px, transparent 6px 12px)" }}
              >
                {dayNumber} <span className="text-[10.5px] font-semibold">Day off</span>
              </div>
            );
          }
          if (date < today) {
            return (
              <div key={date} className="min-h-[76px] p-1.5 text-[12px] font-bold text-line-strong">
                {dayNumber}
              </div>
            );
          }
          const after = date > lastDate;
          const closed = closedDays.has(date);
          const holiday = uaePublicHoliday(date);
          const items = draft.items.filter((item) => item.days.includes(weekday)).sort((a, b) => a.start - b.start);
          const on = items.filter((item) => !draft.closures.some((row) => row.date === date && row.item === item.key));
          const off = items.length - on.length;
          const classes = on.filter((item) => !item.free).length;
          return (
            <button
              key={date}
              type="button"
              onClick={() => onPick(date)}
              aria-pressed={selected === date}
              aria-label={`${formatDateKey(date)}${closed ? ", closed" : after ? ", after the end of the term" : `, ${classes} ${classes === 1 ? "session" : "sessions"}`}${holiday ? `, ${holiday}` : ""}`}
              className={[
                "relative grid min-h-[76px] content-start gap-1 rounded-[10px] border-[1.5px] p-1.5 text-left text-[11px]",
                closed ? "border-cancelled/40 bg-cancelled-surface" : after ? "border-dashed border-line bg-transparent" : holiday ? "border-late bg-paper" : "border-line bg-paper",
                selected === date ? "shadow-[0_0_0_2px_var(--color-ink)]" : "",
              ].join(" ")}
            >
              <span className={["flex justify-between text-[12.5px] font-bold tabular-nums", after ? "text-ink-3" : "text-ink"].join(" ")}>
                {dayNumber}
                {closed || after ? null : <small className="text-[10.5px] font-semibold text-ink-3">{classes}</small>}
              </span>
              {closed ? (
                <span className="text-[10.5px] font-bold text-cancelled">Closed{holiday ? `, ${holiday}` : ""}</span>
              ) : after ? (
                <span className="text-[10.5px] font-bold text-ink-3">After the term</span>
              ) : (
                <>
                  <span className="flex flex-wrap gap-0.5" aria-hidden="true">
                    {on.map((item) => {
                      const color = groups.get(item.groupId)?.color ?? "#5B5F73";
                      return (
                        <i
                          key={item.key}
                          className="h-1.5 w-3.5 rounded-sm"
                          style={{
                            background: item.free ? `repeating-linear-gradient(135deg, ${color} 0 3px, transparent 3px 6px)` : color,
                          }}
                        />
                      );
                    })}
                  </span>
                  {holiday ? <span className="text-[10.5px] font-bold text-late">{holiday}?</span> : null}
                  {off ? <span className="text-[10.5px] font-bold text-cancelled">{off} off</span> : null}
                </>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11.5px] text-ink-3">
        <span>
          <i className="mr-1.5 inline-block h-3 w-3 rounded-sm bg-accent align-[-2px]" aria-hidden="true" />A session
        </span>
        <span>
          <i
            className="mr-1.5 inline-block h-3 w-3 rounded-sm align-[-2px]"
            style={{ background: "repeating-linear-gradient(135deg, var(--color-ink-3) 0 3px, transparent 3px 6px)" }}
            aria-hidden="true"
          />
          Private hours
        </span>
        <span>
          <i className="mr-1.5 inline-block h-3 w-3 rounded-sm border-[1.5px] border-late align-[-2px]" aria-hidden="true" />
          Public holiday
        </span>
        <span>
          <i className="mr-1.5 inline-block h-3 w-3 rounded-sm border-[1.5px] border-cancelled/40 bg-cancelled-surface align-[-2px]" aria-hidden="true" />
          Closed
        </span>
      </div>
    </div>
  );
}
